// C:\IntegraNube_WebStore\src\pages\Contacto.js
'use strict';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useLocation,
} from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  AlternateEmailOutlined as AlternateEmailIcon,
  ChatBubbleOutlineOutlined as MessageIcon,
  CheckCircleOutline as CheckIcon,
  EmailOutlined as EmailIcon,
  LocationOnOutlined as LocationIcon,
  PersonOutlineOutlined as PersonIcon,
  PhoneIphoneOutlined as MobileIcon,
  PhoneOutlined as PhoneIcon,
  ReceiptLongOutlined as PedidoIcon,
  RequestQuoteOutlined as CotizacionIcon,
  SendOutlined as SendIcon,
  ShieldOutlined as ShieldIcon,
  StorefrontOutlined as StorefrontIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

const TURNSTILE_SCRIPT_ID =
  'integranube-turnstile-script';

const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const STORE_API_URL =
  String(
    process.env.REACT_APP_API_URL ||
      'https://store.integranube.cl'
  )
    .trim()
    .replace(/\/+$/, '');

const TIPO_OPTIONS = [
  {
    value: 'general',
    label: 'Consulta general',
    description:
      'Información sobre la tienda, compras, retiro o despacho.',
    icon: <MessageIcon fontSize="small" />,
  },
  {
    value: 'producto',
    label: 'Consulta por producto',
    description:
      'Consulte por el producto que estaba revisando.',
    icon: <StorefrontIcon fontSize="small" />,
  },
  {
    value: 'cotizacion',
    label: 'Solicitar cotización',
    description:
      'Solicite información comercial o una cotización.',
    icon: <CotizacionIcon fontSize="small" />,
  },
  {
    value: 'pedido',
    label: 'Consulta por un pedido',
    description:
      'Consulte por el estado o antecedentes de un pedido.',
    icon: <PedidoIcon fontSize="small" />,
  },
  {
    value: 'otro',
    label: 'Otra consulta',
    description:
      'Para cualquier otro requerimiento.',
    icon: <EmailIcon fontSize="small" />,
  },
];

/* ===================================================================== */
/*  Helpers                                                              */
/* ===================================================================== */

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.branding ||
      source.data.nombre ||
      source.data.contacto
    )
  ) {
    return source.data;
  }

  return source;
}

function cleanString(value) {
  return String(value ?? '').trim();
}

function safeColor(value, fallback) {
  return cleanString(value) || fallback;
}

function isValidEmail(value) {
  const email = cleanString(value);

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    email
  );
}

function buildAddress(direccion = {}) {
  return [
    direccion?.direccion,
    direccion?.comuna,
    direccion?.ciudad,
    direccion?.region,
  ]
    .map(cleanString)
    .filter(Boolean)
    .filter(
      (value, index, array) =>
        array.indexOf(value) === index
    )
    .join(', ');
}

function normalizeWhatsapp(value) {
  return cleanString(value)
    .replace(/\D/g, '');
}

function getCurrentUrl() {
  if (
    typeof window === 'undefined'
  ) {
    return '';
  }

  return cleanString(
    window.location?.href
  );
}

function buildWhatsappMessage({
  nombreTienda,
  form,
}) {
  const nombreCliente =
    cleanString(form?.nombre);

  const consulta =
    cleanString(form?.mensaje);

  const url =
    getCurrentUrl();

  const lines = [
    `Hola 👋, estoy visitando ${nombreTienda} y necesito ayuda con una compra.`,
  ];

  if (nombreCliente) {
    lines.push(
      `Mi nombre es ${nombreCliente}.`
    );
  }

  if (consulta) {
    lines.push('');
    lines.push(
      `Mi consulta es: ${consulta}`
    );
  }

  if (url) {
    lines.push('');
    lines.push(
      `Estoy viendo: ${url}`
    );
  }

  return lines.join('\n');
}

function buildWhatsappUrl({
  telefono,
  nombreTienda,
  form,
}) {
  const number =
    normalizeWhatsapp(telefono);

  if (!number) {
    return '';
  }

  const message =
    buildWhatsappMessage({
      nombreTienda,
      form,
    });

  return (
    `https://wa.me/${number}` +
    `?text=${encodeURIComponent(message)}`
  );
}

function normalizeConfiguredTipos(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((tipo) =>
      cleanString(tipo).toLowerCase()
    )
    .filter(Boolean);
}

/* ===================================================================== */
/*  Turnstile                                                            */
/* ===================================================================== */

function loadTurnstileScript() {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return Promise.reject(
      new Error(
        'Turnstile requiere un navegador.'
      )
    );
  }

  if (window.turnstile) {
    return Promise.resolve(
      window.turnstile
    );
  }

  return new Promise(
    (resolve, reject) => {
      let script =
        document.getElementById(
          TURNSTILE_SCRIPT_ID
        );

      const handleLoad = () => {
        if (window.turnstile) {
          resolve(window.turnstile);
          return;
        }

        reject(
          new Error(
            'Turnstile cargó sin exponer su API.'
          )
        );
      };

      const handleError = () => {
        reject(
          new Error(
            'No fue posible cargar Cloudflare Turnstile.'
          )
        );
      };

      if (script) {
        script.addEventListener(
          'load',
          handleLoad,
          { once: true }
        );

        script.addEventListener(
          'error',
          handleError,
          { once: true }
        );

        return;
      }

      script =
        document.createElement('script');

      script.id =
        TURNSTILE_SCRIPT_ID;

      script.src =
        TURNSTILE_SCRIPT_URL;

      script.async = true;
      script.defer = true;

      script.addEventListener(
        'load',
        handleLoad,
        { once: true }
      );

      script.addEventListener(
        'error',
        handleError,
        { once: true }
      );

      document.head.appendChild(
        script
      );
    }
  );
}

function TurnstileBox({
  siteKey,
  colorPrimario,
  onToken,
  onError,
}) {
  const containerRef =
    useRef(null);

  const widgetIdRef =
    useRef(null);

  useEffect(() => {
    let mounted = true;

    if (!siteKey) {
      onToken('');
      return undefined;
    }

    loadTurnstileScript()
      .then((turnstile) => {
        if (
          !mounted ||
          !containerRef.current
        ) {
          return;
        }

        if (
          widgetIdRef.current !== null
        ) {
          try {
            turnstile.remove(
              widgetIdRef.current
            );
          } catch (_) {
            // Limpieza defensiva.
          }

          widgetIdRef.current = null;
        }

        widgetIdRef.current =
          turnstile.render(
            containerRef.current,
            {
              sitekey: siteKey,
              theme: 'auto',
              size: 'flexible',
              action: 'contacto',

              callback: (token) => {
                if (mounted) {
                  onToken(
                    cleanString(token)
                  );
                }
              },

              'expired-callback': () => {
                if (mounted) {
                  onToken('');
                }
              },

              'timeout-callback': () => {
                if (mounted) {
                  onToken('');
                }
              },

              'error-callback': (
                errorCode
              ) => {
                if (mounted) {
                  onToken('');

                  onError(
                    errorCode
                      ? `No fue posible completar la verificación (${errorCode}).`
                      : 'No fue posible completar la verificación.'
                  );
                }
              },
            }
          );
      })
      .catch((error) => {
        if (mounted) {
          onToken('');

          onError(
            error?.message ||
              'No fue posible cargar Turnstile.'
          );
        }
      });

    return () => {
      mounted = false;

      if (
        window.turnstile &&
        widgetIdRef.current !== null
      ) {
        try {
          window.turnstile.remove(
            widgetIdRef.current
          );
        } catch (_) {
          // Limpieza defensiva.
        }
      }

      widgetIdRef.current = null;
    };
  }, [
    siteKey,
    onToken,
    onError,
  ]);

  return (
    <Box>
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          minHeight: 65,

          '& iframe': {
            maxWidth: '100%',
          },
        }}
      />

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          mt: 0.75,
          color: '#64748b',
        }}
      >
        <ShieldIcon
          sx={{
            fontSize: 17,
            color: colorPrimario,
          }}
        />

        <Typography
          variant="caption"
        >
          Verificación de seguridad protegida por Cloudflare.
        </Typography>
      </Stack>
    </Box>
  );
}

/* ===================================================================== */
/*  Contacto                                                             */
/* ===================================================================== */

export default function Contacto({
  tienda = null,
}) {
  const location =
    useLocation();

  const data =
    useMemo(
      () =>
        resolveTiendaData(tienda),
      [tienda]
    );

  const nombreTienda =
    cleanString(
      data?.nombre
    ) ||
    'nuestra tienda';

  const colorPrimario =
    safeColor(
      data?.branding?.colorPrimario,
      '#1976d2'
    );

  const colorSecundario =
    safeColor(
      data?.branding?.colorSecundario,
      '#111827'
    );

  const contacto =
    data?.contacto || {};

  const direccion =
    data?.direccion || {};

  const direccionCompleta =
    buildAddress(direccion);

  const whatsappNumber =
    normalizeWhatsapp(
      contacto?.whatsapp
    );

  const formularioConfig =
    contacto?.formulario || {};

  const formularioHabilitado =
    formularioConfig?.habilitado === true;

  /*
   * Cuando se llega desde BotonContactoFlotante conservamos la página
   * original y, si corresponde, el producto real que estaba viendo
   * el cliente.
   */
  const contextoNavegacion =
    location?.state
      ?.contactoContexto &&
    typeof location
      .state
      .contactoContexto ===
      'object'
      ? location
          .state
          .contactoContexto
      : null;

  const productoContextoId =
    Number(
      contextoNavegacion
        ?.productoId
    );

  const tieneProductoContexto =
    Number.isSafeInteger(
      productoContextoId
    ) &&
    productoContextoId > 0;

  const paginaOrigenContexto =
    cleanString(
      contextoNavegacion
        ?.paginaOrigen
    );

  const configuredTipos =
    useMemo(
      () =>
        normalizeConfiguredTipos(
          formularioConfig?.tipos
        ),
      [formularioConfig?.tipos]
    );

  /*
   * El tipo "producto" sólo se ofrece cuando el visitante llegó con un
   * producto real resuelto desde la ficha. Así nunca se solicita al
   * cliente un ID interno.
   */
  const tiposDisponibles =
    useMemo(() => {
      const allowedOnContactPage =
        TIPO_OPTIONS.filter(
          (option) =>
            option.value !==
              'producto' ||
            tieneProductoContexto
        );

      if (
        configuredTipos.length === 0
      ) {
        return allowedOnContactPage;
      }

      return allowedOnContactPage.filter(
        (option) =>
          configuredTipos.includes(
            option.value
          )
      );
    }, [
      configuredTipos,
      tieneProductoContexto,
    ]);

  const turnstileHabilitado =
    data?.seguridad?.turnstile !== false;

  const turnstileSiteKey =
    turnstileHabilitado
      ? (
          cleanString(
            data?.seguridad
              ?.turnstileSiteKey
          ) ||
          cleanString(
            process.env
              .REACT_APP_TURNSTILE_SITE_KEY
          )
        )
      : '';

  const tipoContexto =
    cleanString(
      contextoNavegacion?.tipo
    )?.toLowerCase();

  const initialTipo =
    tiposDisponibles.some(
      (option) =>
        option.value ===
        tipoContexto
    )
      ? tipoContexto
      : (
          tiposDisponibles[0]
            ?.value ||
          'general'
        );

  const [
    form,
    setForm,
  ] = useState({
    tipo: initialTipo,
    nombre: '',
    email: '',
    telefono: '',
    pedidoId: '',
    mensaje: '',
  });

  const [
    sending,
    setSending,
  ] = useState(false);

  const [
    sentSuccessfully,
    setSentSuccessfully,
  ] = useState(false);

  const [
    turnstileToken,
    setTurnstileToken,
  ] = useState('');

  const [
    turnstileError,
    setTurnstileError,
  ] = useState('');

  const [
    turnstileResetKey,
    setTurnstileResetKey,
  ] = useState(0);

  const [
    snack,
    setSnack,
  ] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    if (
      tiposDisponibles.length === 0
    ) {
      return;
    }

    const currentAllowed =
      tiposDisponibles.some(
        (option) =>
          option.value === form.tipo
      );

    if (!currentAllowed) {
      setForm((prev) => ({
        ...prev,
        tipo:
          tiposDisponibles[0].value,
        pedidoId: '',
      }));
    }
  }, [
    tiposDisponibles,
    form.tipo,
  ]);

  const whatsappUrl =
    useMemo(
      () =>
        buildWhatsappUrl({
          telefono:
            contacto?.whatsapp,
          nombreTienda,
          form,
        }),
      [
        contacto?.whatsapp,
        nombreTienda,
        form,
      ]
    );

  const selectedTipo =
    useMemo(
      () =>
        tiposDisponibles.find(
          (option) =>
            option.value === form.tipo
        ) ||
        tiposDisponibles[0] ||
        null,
      [
        tiposDisponibles,
        form.tipo,
      ]
    );

  const updateField =
    (field, value) => {
      setSentSuccessfully(false);

      setForm(
        (prev) => ({
          ...prev,
          [field]: value,
        })
      );
    };

  const handleTurnstileToken =
    useCallback((token) => {
      setTurnstileToken(token);
      setTurnstileError('');
    }, []);

  const handleTurnstileError =
    useCallback((message) => {
      setTurnstileError(
        cleanString(message) ||
          'No fue posible completar la verificación de seguridad.'
      );
    }, []);

  const showSnack = (
    message,
    severity = 'info'
  ) => {
    setSnack({
      open: true,
      message,
      severity,
    });
  };

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileError('');
    setTurnstileResetKey(
      (value) => value + 1
    );
  };

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      if (
        !formularioHabilitado ||
        tiposDisponibles.length === 0
      ) {
        showSnack(
          'El formulario no está disponible para esta tienda.',
          'warning'
        );
        return;
      }

      if (
        !cleanString(form.nombre) ||
        !cleanString(form.email) ||
        !cleanString(form.mensaje)
      ) {
        showSnack(
          'Complete su nombre, correo electrónico y mensaje.',
          'warning'
        );
        return;
      }

      if (
        !isValidEmail(form.email)
      ) {
        showSnack(
          'Ingrese un correo electrónico válido.',
          'warning'
        );
        return;
      }

      if (
        cleanString(form.mensaje)
          .length < 5
      ) {
        showSnack(
          'Agregue un poco más de información a su consulta.',
          'warning'
        );
        return;
      }

      if (
        form.tipo === 'pedido'
      ) {
        const pedidoId =
          Number(form.pedidoId);

        if (
          !Number.isSafeInteger(
            pedidoId
          ) ||
          pedidoId <= 0
        ) {
          showSnack(
            'Ingrese un número de pedido válido.',
            'warning'
          );
          return;
        }
      }

      if (
        turnstileHabilitado &&
        !turnstileSiteKey
      ) {
        showSnack(
          'La verificación de seguridad no está configurada.',
          'warning'
        );
        return;
      }

      if (
        turnstileHabilitado &&
        !turnstileToken
      ) {
        showSnack(
          'Complete la verificación de seguridad antes de enviar.',
          'warning'
        );
        return;
      }

      const payload = {
        tipo:
          form.tipo,

        nombre:
          cleanString(
            form.nombre
          ),

        email:
          cleanString(
            form.email
          ),

        telefono:
          cleanString(
            form.telefono
          ) ||
          null,

        mensaje:
          cleanString(
            form.mensaje
          ),

        productoId:
          form.tipo === 'producto' &&
          tieneProductoContexto
            ? productoContextoId
            : null,

        pedidoId:
          form.tipo === 'pedido'
            ? Number(
                form.pedidoId
              )
            : null,

        paginaOrigen:
          paginaOrigenContexto ||
          getCurrentUrl() ||
          null,

        turnstileToken:
          turnstileHabilitado
            ? turnstileToken
            : null,
      };

      setSending(true);
      setSentSuccessfully(false);

      try {
        const response =
          await fetch(
            `${STORE_API_URL}/consultas`,
            {
              method: 'POST',
              headers: {
                'Content-Type':
                  'application/json',
              },
              body:
                JSON.stringify(
                  payload
                ),
            }
          );

        const raw =
          await response.text();

        let result = null;

        try {
          result =
            raw
              ? JSON.parse(raw)
              : null;
        } catch (_) {
          result = null;
        }

        if (!response.ok) {
          throw new Error(
            cleanString(
              result?.message
            ) ||
            cleanString(
              result?.error?.message
            ) ||
            (
              typeof result?.error ===
              'string'
                ? result.error
                : ''
            ) ||
            'No fue posible enviar la consulta.'
          );
        }

        setSentSuccessfully(true);

        setForm({
          tipo:
            initialTipo,
          nombre: '',
          email: '',
          telefono: '',
          pedidoId: '',
          mensaje: '',
        });

        showSnack(
          'Su consulta fue enviada correctamente.',
          'success'
        );
      } catch (error) {
        showSnack(
          cleanString(
            error?.message
          ) ||
            'No fue posible enviar la consulta. Intente nuevamente.',
          'error'
        );
      } finally {
        setSending(false);

        if (turnstileHabilitado) {
          resetTurnstile();
        }
      }
    };

  const showEmailAsForm =
    formularioHabilitado;

  const submitDisabled =
    sending ||
    !formularioHabilitado ||
    tiposDisponibles.length === 0 ||
    (
      turnstileHabilitado &&
      (
        !turnstileSiteKey ||
        !turnstileToken
      )
    );

  return (
    <Box
      sx={{
        bgcolor: '#f8fafc',
        minHeight:
          'calc(100vh - 80px)',
      }}
    >
      {/* =============================================================== */}
      {/* Encabezado                                                       */}
      {/* =============================================================== */}

      <Box
        component="section"
        sx={{
          borderBottom:
            '1px solid #e5e7eb',
          background: `
            radial-gradient(circle at 12% 25%, ${colorPrimario}16 0, transparent 30%),
            radial-gradient(circle at 88% 15%, ${colorSecundario}0d 0, transparent 30%),
            #ffffff
          `,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            py: {
              xs: 4,
              md: 5.5,
            },
          }}
        >
          <Stack
            spacing={1.25}
            alignItems="center"
            textAlign="center"
          >
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                bgcolor:
                  `${colorPrimario}12`,
                color:
                  colorPrimario,
              }}
            >
              <MessageIcon
                sx={{
                  fontSize: 31,
                }}
              />
            </Box>

            <Typography
              component="h1"
              sx={{
                color: '#0f172a',
                fontSize: {
                  xs: 30,
                  md: 40,
                },
                lineHeight: 1.1,
                fontWeight: 950,
                letterSpacing:
                  '-0.04em',
              }}
            >
              ¿Necesita ayuda?
            </Typography>

            <Typography
              sx={{
                maxWidth: 680,
                color: '#64748b',
                fontSize: {
                  xs: 15,
                  md: 17,
                },
                lineHeight: 1.65,
              }}
            >
              Envíe una consulta a {nombreTienda}. El mensaje llegará
              directamente al equipo de la empresa para continuar la
              conversación por correo electrónico.
            </Typography>

            {whatsappNumber ? (
              <Button
                component="a"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={
                  <WhatsAppIcon />
                }
                sx={{
                  mt: 0.75,
                  minHeight: 46,
                  px: 2.5,
                  borderRadius: 2,
                  bgcolor: '#25D366',
                  textTransform:
                    'none',
                  fontWeight: 900,
                  boxShadow: 'none',

                  '&:hover': {
                    bgcolor:
                      '#1fb858',
                    boxShadow: 'none',
                  },
                }}
              >
                Contactar por WhatsApp
              </Button>
            ) : null}
          </Stack>
        </Container>
      </Box>

      {/* =============================================================== */}
      {/* Contenido                                                        */}
      {/* =============================================================== */}

      <Container
        maxWidth="lg"
        sx={{
          py: {
            xs: 3,
            md: 5,
          },
        }}
      >
        <Grid
          container
          spacing={{
            xs: 2,
            md: 2.5,
          }}
          alignItems="stretch"
        >
          {/* =========================================================== */}
          {/* Canales                                                     */}
          {/* =========================================================== */}

          <Grid
            item
            xs={12}
            md={5}
          >
            <Stack
              spacing={2}
              sx={{
                height: '100%',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: {
                    xs: 2,
                    md: 2.5,
                  },
                  border:
                    '1px solid #e5e7eb',
                  borderRadius: 3,
                  bgcolor: '#fff',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems:
                        'center',
                      bgcolor:
                        `${colorPrimario}10`,
                      color:
                        colorPrimario,
                    }}
                  >
                    <StorefrontIcon />
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={950}
                      sx={{
                        color:
                          '#0f172a',
                      }}
                    >
                      Canales disponibles
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Utilice el canal habilitado por la tienda.
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  spacing={1}
                  sx={{
                    mt: 2.25,
                  }}
                >
                  {formularioHabilitado ? (
                    <ContactAction
                      icon={
                        <EmailIcon />
                      }
                      title="Consulta por correo"
                      subtitle="Complete el formulario y la empresa podrá responder directamente a su email."
                      color={
                        colorPrimario
                      }
                    />
                  ) : null}

                  {whatsappNumber ? (
                    <ContactAction
                      icon={
                        <WhatsAppIcon />
                      }
                      title="WhatsApp"
                      subtitle="Abrir conversación por WhatsApp"
                      href={whatsappUrl}
                      color="#25D366"
                      external
                    />
                  ) : null}

                  {!showEmailAsForm &&
                  contacto.email ? (
                    <ContactAction
                      icon={
                        <EmailIcon />
                      }
                      title="Email"
                      subtitle={
                        contacto.email
                      }
                      href={`mailto:${contacto.email}`}
                      color={
                        colorPrimario
                      }
                    />
                  ) : null}

                  {contacto.telefono ? (
                    <ContactAction
                      icon={
                        <PhoneIcon />
                      }
                      title="Teléfono"
                      subtitle={
                        contacto.telefono
                      }
                      href={`tel:${String(
                        contacto.telefono
                      ).replace(
                        /\s+/g,
                        ''
                      )}`}
                      color={
                        colorPrimario
                      }
                    />
                  ) : null}

                  {direccionCompleta ? (
                    <ContactAction
                      icon={
                        <LocationIcon />
                      }
                      title="Ubicación"
                      subtitle={
                        direccionCompleta
                      }
                      color={
                        colorPrimario
                      }
                    />
                  ) : null}

                  {!formularioHabilitado &&
                  !whatsappNumber &&
                  !contacto.email &&
                  !contacto.telefono &&
                  !direccionCompleta ? (
                    <Alert
                      severity="info"
                    >
                      La tienda no ha publicado canales de contacto.
                    </Alert>
                  ) : null}
                </Stack>
              </Paper>

              {formularioHabilitado ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: {
                      xs: 2,
                      md: 2.5,
                    },
                    border:
                      '1px solid #dbeafe',
                    borderRadius: 3,
                    bgcolor: '#eff6ff',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="flex-start"
                  >
                    <EmailIcon
                      sx={{
                        color:
                          colorPrimario,
                        mt: 0.15,
                      }}
                    />

                    <Box>
                      <Typography
                        fontWeight={900}
                        sx={{
                          color:
                            '#1e3a8a',
                        }}
                      >
                        Respuesta directa por email
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          color:
                            '#475569',
                          lineHeight: 1.55,
                        }}
                      >
                        La consulta se envía con sus datos de contacto.
                        La empresa podrá responder directamente a la
                        dirección de correo indicada.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          </Grid>

          {/* =========================================================== */}
          {/* Formulario                                                  */}
          {/* =========================================================== */}

          <Grid
            item
            xs={12}
            md={7}
          >
            {formularioHabilitado &&
            tiposDisponibles.length > 0 ? (
              <Paper
                component="form"
                onSubmit={
                  handleSubmit
                }
                elevation={0}
                sx={{
                  p: {
                    xs: 2,
                    sm: 2.5,
                    md: 3,
                  },
                  height: '100%',
                  border:
                    '1px solid #e5e7eb',
                  borderRadius: 3,
                  bgcolor: '#fff',
                }}
              >
                <Stack
                  direction={{
                    xs: 'column',
                    sm: 'row',
                  }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{
                    xs: 'flex-start',
                    sm: 'center',
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={950}
                      sx={{
                        color:
                          '#0f172a',
                      }}
                    >
                      Enviar una consulta
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.35,
                      }}
                    >
                      Seleccione el motivo y complete sus datos.
                    </Typography>
                  </Box>

                  <Chip
                    icon={
                      <ShieldIcon
                        sx={{
                          fontSize:
                            '16px !important',
                        }}
                      />
                    }
                    label="Formulario protegido"
                    size="small"
                    variant="outlined"
                    sx={{
                      fontWeight: 800,
                      borderColor:
                        '#dbe4ee',
                    }}
                  />
                </Stack>

                <Divider
                  sx={{
                    my: 2.25,
                  }}
                />

                <Stack
                  spacing={1.5}
                >
                  <TextField
                    fullWidth
                    select
                    label="Motivo de la consulta"
                    value={
                      form.tipo
                    }
                    onChange={(e) => {
                      updateField(
                        'tipo',
                        e.target.value
                      );

                      if (
                        e.target.value !==
                        'pedido'
                      ) {
                        updateField(
                          'pedidoId',
                          ''
                        );
                      }
                    }}
                  >
                    {tiposDisponibles.map(
                      (option) => (
                        <MenuItem
                          key={
                            option.value
                          }
                          value={
                            option.value
                          }
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                display:
                                  'grid',
                                placeItems:
                                  'center',
                                color:
                                  colorPrimario,
                              }}
                            >
                              {option.icon}
                            </Box>

                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={800}
                              >
                                {option.label}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.description}
                              </Typography>
                            </Box>
                          </Stack>
                        </MenuItem>
                      )
                    )}
                  </TextField>

                  {selectedTipo ? (
                    <Alert
                      severity="info"
                      icon={
                        selectedTipo.icon
                      }
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      {selectedTipo.description}
                    </Alert>
                  ) : null}

                  {tieneProductoContexto ? (
                    <Alert
                      severity="success"
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={900}
                      >
                        Producto consultado
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.25,
                        }}
                      >
                        {cleanString(
                          contextoNavegacion
                            ?.productoNombre
                        ) ||
                          `Producto #${productoContextoId}`}

                        {cleanString(
                          contextoNavegacion
                            ?.productoSku
                        )
                          ? ` · SKU ${cleanString(
                              contextoNavegacion
                                ?.productoSku
                            )}`
                          : ''}
                      </Typography>

                      {paginaOrigenContexto ? (
                        <Link
                          href={
                            paginaOrigenContexto
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            display:
                              'inline-block',
                            mt: 0.5,
                            fontSize: 13,
                            fontWeight: 800,
                          }}
                        >
                          Ver página de origen
                        </Link>
                      ) : null}
                    </Alert>
                  ) : paginaOrigenContexto ? (
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      La consulta conservará como referencia la página
                      desde donde se inició el contacto.
                    </Alert>
                  ) : null}

                  <TextField
                    fullWidth
                    required
                    label="Nombre"
                    autoComplete="name"
                    value={
                      form.nombre
                    }
                    onChange={(e) =>
                      updateField(
                        'nombre',
                        e.target.value
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon
                            sx={{
                              color:
                                '#94a3b8',
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                      },
                      gap: 1.5,
                    }}
                  >
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Correo electrónico"
                      autoComplete="email"
                      value={
                        form.email
                      }
                      onChange={(e) =>
                        updateField(
                          'email',
                          e.target.value
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AlternateEmailIcon
                              sx={{
                                color:
                                  '#94a3b8',
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Teléfono (opcional)"
                      autoComplete="tel"
                      value={
                        form.telefono
                      }
                      onChange={(e) =>
                        updateField(
                          'telefono',
                          e.target.value
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MobileIcon
                              sx={{
                                color:
                                  '#94a3b8',
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {form.tipo === 'pedido' ? (
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Número de pedido"
                      value={
                        form.pedidoId
                      }
                      onChange={(e) =>
                        updateField(
                          'pedidoId',
                          e.target.value
                        )
                      }
                      inputProps={{
                        min: 1,
                        step: 1,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PedidoIcon
                              sx={{
                                color:
                                  '#94a3b8',
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ) : null}

                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={5}
                    label="Mensaje"
                    value={
                      form.mensaje
                    }
                    onChange={(e) =>
                      updateField(
                        'mensaje',
                        e.target.value
                      )
                    }
                    placeholder="Describa brevemente su consulta..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{
                            alignSelf:
                              'flex-start',
                            mt: 1.2,
                          }}
                        >
                          <MessageIcon
                            sx={{
                              color:
                                '#94a3b8',
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {sentSuccessfully ? (
                    <Alert
                      severity="success"
                      icon={
                        <CheckIcon />
                      }
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      Su consulta fue recibida correctamente.
                    </Alert>
                  ) : null}

                  {turnstileHabilitado ? (
                    <Box
                      sx={{
                        mt:
                          '4px !important',
                        p: {
                          xs: 1.5,
                          sm: 2,
                        },
                        border:
                          '1px solid #eef2f7',
                        borderRadius: 2.5,
                        bgcolor:
                          '#f8fafc',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={900}
                        sx={{
                          mb: 1.25,
                          color:
                            '#334155',
                        }}
                      >
                        Verificación de seguridad
                      </Typography>

                      {turnstileSiteKey ? (
                        <TurnstileBox
                          key={
                            turnstileResetKey
                          }
                          siteKey={
                            turnstileSiteKey
                          }
                          colorPrimario={
                            colorPrimario
                          }
                          onToken={
                            handleTurnstileToken
                          }
                          onError={
                            handleTurnstileError
                          }
                        />
                      ) : (
                        <Alert
                          severity="warning"
                        >
                          La verificación de seguridad está habilitada,
                          pero la Site Key de Turnstile no está disponible.
                        </Alert>
                      )}

                      {turnstileError ? (
                        <Alert
                          severity="warning"
                          sx={{
                            mt: 1.25,
                            borderRadius: 2,
                          }}
                        >
                          {turnstileError}
                        </Alert>
                      ) : null}

                      {turnstileToken ? (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{
                            mt: 1,
                            color:
                              '#059669',
                          }}
                        >
                          <CheckIcon
                            sx={{
                              fontSize: 18,
                            }}
                          />

                          <Typography
                            variant="body2"
                            fontWeight={800}
                          >
                            Verificación completada
                          </Typography>
                        </Stack>
                      ) : null}
                    </Box>
                  ) : null}

                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'row',
                    }}
                    spacing={1.25}
                    justifyContent="flex-end"
                  >
                    {whatsappNumber ? (
                      <Button
                        component="a"
                        href={
                          whatsappUrl
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={
                          <WhatsAppIcon />
                        }
                        disabled={
                          sending
                        }
                        sx={{
                          minHeight: 46,
                          px: 2.25,
                          borderRadius: 2,
                          borderColor:
                            '#25D366',
                          color:
                            '#15803d',
                          textTransform:
                            'none',
                          fontWeight: 900,

                          '&:hover': {
                            borderColor:
                              '#16a34a',
                            bgcolor:
                              '#f0fdf4',
                          },
                        }}
                      >
                        Usar WhatsApp
                      </Button>
                    ) : null}

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={
                        sending ? (
                          <CircularProgress
                            size={18}
                            color="inherit"
                          />
                        ) : (
                          <SendIcon />
                        )
                      }
                      disabled={
                        submitDisabled
                      }
                      sx={{
                        minHeight: 46,
                        px: 2.5,
                        borderRadius: 2,
                        bgcolor:
                          colorPrimario,
                        textTransform:
                          'none',
                        fontWeight: 900,
                        boxShadow: 'none',

                        '&:hover': {
                          bgcolor:
                            colorPrimario,
                          filter:
                            'brightness(0.92)',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {sending
                        ? 'Enviando...'
                        : 'Enviar consulta'}
                    </Button>
                  </Stack>

                  {turnstileHabilitado &&
                  !turnstileToken &&
                  turnstileSiteKey ? (
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: {
                          xs: 'left',
                          sm: 'right',
                        },
                        color:
                          '#94a3b8',
                      }}
                    >
                      Complete la verificación para habilitar el envío.
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: {
                    xs: 2.5,
                    md: 3,
                  },
                  height: '100%',
                  border:
                    '1px solid #e5e7eb',
                  borderRadius: 3,
                  bgcolor: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Stack
                  spacing={1.25}
                  alignItems="center"
                  textAlign="center"
                  sx={{
                    maxWidth: 480,
                  }}
                >
                  <EmailIcon
                    sx={{
                      fontSize: 42,
                      color:
                        '#94a3b8',
                    }}
                  />

                  <Typography
                    variant="h6"
                    fontWeight={900}
                  >
                    Formulario no disponible
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Esta tienda no tiene habilitado el formulario de
                    consultas para esta sección. Utilice alguno de los
                    canales disponibles.
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() =>
          setSnack(
            (prev) => ({
              ...prev,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={
            snack.severity
          }
          variant="filled"
          onClose={() =>
            setSnack(
              (prev) => ({
                ...prev,
                open: false,
              })
            )
          }
          sx={{
            width: '100%',
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ===================================================================== */
/*  Componentes auxiliares                                               */
/* ===================================================================== */

function ContactAction({
  icon,
  title,
  subtitle,
  href = '',
  color,
  external = false,
}) {
  const content = (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
          bgcolor:
            `${color}12`,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          fontWeight={900}
          sx={{
            color: '#0f172a',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 0.15,
            color: '#64748b',
            lineHeight: 1.45,
            overflowWrap:
              'anywhere',
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );

  if (!href) {
    return (
      <Box
        sx={{
          p: 1.25,
          border:
            '1px solid #eef2f7',
          borderRadius: 2.5,
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Link
      href={href}
      target={
        external
          ? '_blank'
          : undefined
      }
      rel={
        external
          ? 'noopener noreferrer'
          : undefined
      }
      underline="none"
      sx={{
        p: 1.25,
        border:
          '1px solid #eef2f7',
        borderRadius: 2.5,
        transition:
          'border-color 140ms ease, background-color 140ms ease',

        '&:hover': {
          bgcolor:
            '#f8fafc',
          borderColor:
            '#dbe4ee',
        },
      }}
    >
      {content}
    </Link>
  );
}
